import React from 'react';
import Feature from '../feature/Feature';
import MySlider from '../../../../components/MySlider';
import { ReactComponent as InsightMessageHighlight } from '../../../../assets/img_insight_message_highlight.svg';
import { InputText } from 'primereact/inputtext';
import { ColorPicker } from 'primereact/colorpicker';
import InsightMessages from './insightMessages/InsightMessages';
import { type paths } from '../../../../endpoint/Schema';
import { Icons } from '../../../../assets/icons';

interface IInsightFeatureProps {
  insightScores: number[];
  setInsightScores: React.Dispatch<React.SetStateAction<number[]>>;
  insightSafeScoreColor: string;
  setInsightSafeScoreColor: React.Dispatch<React.SetStateAction<string>>;
  insightAlertScoreColor: string;
  setInsightAlertScoreColor: React.Dispatch<React.SetStateAction<string>>;
  insightDangerScoreColor: string;
  setInsightDangerScoreColor: React.Dispatch<React.SetStateAction<string>>;

  featureSettings:
    | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
    | undefined;
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<
      | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
      | undefined
    >
  >;
}

export default function InsightFeature({
  insightScores,
  setInsightScores,
  insightSafeScoreColor,
  setInsightSafeScoreColor,
  insightAlertScoreColor,
  setInsightAlertScoreColor,
  insightDangerScoreColor,
  setInsightDangerScoreColor,
  featureSettings,
  setFeatureSettings,
}: IInsightFeatureProps) {
  return (
    <Feature
      icon={Icons.ic_insight}
      image="img_insight"
      title="Insight"
      description="This feature is crafted to offer users personalized insights based on their driving score in each area, guiding them on how to maintain or improve their performance. These insights provide valuable recommendations tailored to each user's driving habits, empowering them to drive safely and efficiently."
      isMandatory
      checked
    >
      <div className="max-md:max-w-full">
        <div className="w-full px-20">
          <div className="w-[80%] text-xl text-[color:var(--color-stormGray)]">
            Please use the form below to specify the score ranges for different
            status levels. The scores will be categorized into three statuses:
            Safe, Alert, and Danger. Indicate the minimum and maximum values for
            each range:
          </div>

          <div className="flex flex-row w-full justify-between mt-10">
            <div className="text-2xl">
              <div className="mb-6 text-[color:var(--color-stormGray)]">
                Specify the range
              </div>
              <table className="rounded-3xl bg-whiteSmoke px-5 py-8">
                <tbody>
                  <tr className="h-8" />
                  <tr>
                    <td className="w-16" />
                    <td className="pr-12">Safe state</td>
                    <td className="flex justify-center items-center pr-16">
                      <MySlider
                        className="mt-6"
                        track="inverted"
                        valueLabelDisplay="on"
                        value={insightScores[1]}
                        onChange={(e, value) => {
                          if (Array.isArray(value)) return;
                          if (value < insightScores[0]) return;
                          setInsightScores([insightScores[0], value]);
                        }}
                        min={0}
                        max={100}
                        marks={[
                          {
                            value: 0,
                            label: '0',
                          },
                          {
                            value: 100,
                            label: '100',
                          },
                        ]}
                      />
                    </td>
                    <td>
                      <div className="p-inputgroup">
                        <InputText
                          value={insightSafeScoreColor}
                          className="p-inputtext-sm"
                          onChange={(e) => {
                            const newValue = `${e.target.value.startsWith('#') ? '' : '#'}${e.target.value}`;
                            setInsightSafeScoreColor(newValue);
                          }}
                        />
                        <ColorPicker
                          className="p-inputgroup-addon w-2"
                          format="hex"
                          value={insightSafeScoreColor}
                          onChange={(e) => {
                            const newValue = `#${e.value?.toString() ?? ''}`;
                            setInsightSafeScoreColor(newValue);
                          }}
                        />
                      </div>
                    </td>
                    <td className="w-20" />
                  </tr>
                  <tr>
                    <td />
                    <td className="border-b-[1px]" />
                    <td className="border-b-[1px]" />
                    <td className="border-b-[1px]" />
                    <td />
                  </tr>
                  <tr className="h-6" />
                  <tr>
                    <td />
                    <td className="pr-12">Alert state</td>
                    <td className="flex justify-center items-center pr-16">
                      <MySlider
                        className="mt-6"
                        valueLabelDisplay="on"
                        value={[insightScores[0], insightScores[1] - 1]}
                        onChange={(e, value, activeThumb) => {
                          if (!Array.isArray(value)) return;
                          setInsightScores([value[0], value[1] + 1]);
                        }}
                        min={0}
                        max={100}
                        disableSwap
                        marks={[
                          {
                            value: 0,
                            label: '0',
                          },
                          {
                            value: 100,
                            label: '100',
                          },
                        ]}
                      />
                    </td>
                    <td>
                      <div className="p-inputgroup">
                        <InputText
                          value={insightAlertScoreColor}
                          className="p-inputtext-sm"
                          onChange={(e) => {
                            const newValue = `${e.target.value.startsWith('#') ? '' : '#'}${e.target.value}`;
                            setInsightAlertScoreColor(newValue);
                          }}
                        />
                        <ColorPicker
                          className="p-inputgroup-addon w-2"
                          format="hex"
                          value={insightAlertScoreColor}
                          onChange={(e) => {
                            const newValue = `#${e.value?.toString() ?? ''}`;
                            setInsightAlertScoreColor(newValue);
                          }}
                        />
                      </div>
                    </td>
                    <td />
                  </tr>
                  <tr>
                    <td />
                    <td className="border-b-[1px]" />
                    <td className="border-b-[1px]" />
                    <td className="border-b-[1px]" />
                    <td />
                  </tr>
                  <tr className="h-6" />
                  <tr>
                    <td />
                    <td className="pr-12">Danger state</td>
                    <td className="flex justify-center items-center pr-16">
                      <MySlider
                        className="mt-6"
                        valueLabelDisplay="on"
                        value={insightScores[0] - 1}
                        onChange={(e, value, activeThumb) => {
                          if (Array.isArray(value)) return;
                          if (value > insightScores[1]) return;
                          setInsightScores([value + 1, insightScores[1]]);
                        }}
                        min={0}
                        max={100}
                        marks={[
                          {
                            value: 0,
                            label: '0',
                          },
                          {
                            value: 100,
                            label: '100',
                          },
                        ]}
                      />
                    </td>
                    <td>
                      <div className="p-inputgroup">
                        <InputText
                          value={insightDangerScoreColor}
                          className="p-inputtext-sm"
                          onChange={(e) => {
                            const newValue = `${e.target.value.startsWith('#') ? '' : '#'}${e.target.value}`;
                            setInsightDangerScoreColor(newValue);
                          }}
                        />
                        <ColorPicker
                          className="p-inputgroup-addon w-2"
                          format="hex"
                          value={insightDangerScoreColor}
                          onChange={(e) => {
                            const newValue = `#${e.value?.toString() ?? ''}`;
                            setInsightDangerScoreColor(newValue);
                          }}
                        />
                      </div>
                    </td>
                    <td />
                  </tr>
                  <tr className="h-8" />
                </tbody>
              </table>
            </div>
            <InsightMessageHighlight className="h-80 w-auto" />
          </div>
        </div>

        <InsightMessages
          title="Cornering"
          description="Please use the form below to define insights for each status level of the cornering sub score. Each insight include a Main Message and a Hint."
          safeMessagePlaceholder="Some people corner like a stroke of Van Gogh’s paintbrush!"
          safeHintPlaceholder="We love these people!"
          alertMessagePlaceholder="Silky smooth  cornering is usually safer."
          alertHintPlaceholder="Be careful taking corners."
          dangerMessagePlaceholder="Your cornering should be smoother."
          dangerHintPlaceholder="Go easy on that steering wheel."
          className="my-8"
          insightSettingsText={
            featureSettings?.insightSettings?.insightSettingsText ?? null
          }
          insightType="Cornering"
          setFeatureSettings={setFeatureSettings}
        />
        <InsightMessages
          title="Braking"
          description="Please use the form below to define insights for each status level of the braking sub score. Each insight include a Main Message and a Hint."
          safeMessagePlaceholder="Your Driverly squirrel loves smooth braking."
          safeHintPlaceholder="As long as you don’t disturb him, he’s happy to share his acorns!"
          alertMessagePlaceholder="Harsh braking frighten your Driverly squirrel."
          alertHintPlaceholder="Drive a bit more gently to get his acorns. "
          dangerMessagePlaceholder="Yikes!"
          dangerHintPlaceholder="Try not to slam on your brakes."
          className="my-8"
          insightSettingsText={
            featureSettings?.insightSettings?.insightSettingsText ?? null
          }
          insightType="Braking"
          setFeatureSettings={setFeatureSettings}
        />
        <InsightMessages
          title="Speed"
          description="Please use the form below to define insights for each status level of the braking sub score. Each insight include a Main Message and a Hint."
          safeMessagePlaceholder="Better safe than sorry, huh?"
          safeHintPlaceholder="Good job on keeping at a safe speed."
          alertMessagePlaceholder="Lewis Hamilton is great, but do you know how much he pays for his car insurance? "
          alertHintPlaceholder="We’re familiar with the temptation, but it’s much safer if you ease off on the pedal."
          dangerMessagePlaceholder="Can you smell burning?"
          dangerHintPlaceholder="It’s much safer not to hurtle around. Go easy on the gas."
          className="my-8"
          insightSettingsText={
            featureSettings?.insightSettings?.insightSettingsText ?? null
          }
          insightType="Speed"
          setFeatureSettings={setFeatureSettings}
        />
        <InsightMessages
          title="Acceleration"
          description="Please use the form below to define insights for each status level of the acceleration sub score. Each insight include a Main Message and a Hint."
          safeMessagePlaceholder="Your Driverly squirrel is happy to give away his acorns to keep your acceleration  calm and nice. "
          safeHintPlaceholder="Well done on your smooth driving. "
          alertMessagePlaceholder="Who doesn’t like  great acceleration on a nice car?"
          alertHintPlaceholder="Well, your car insurer! "
          dangerMessagePlaceholder="Those G-forces are no good for you!"
          dangerHintPlaceholder="Smooth acceleration is less likely to put you at risk."
          className="my-8"
          insightSettingsText={
            featureSettings?.insightSettings?.insightSettingsText ?? null
          }
          insightType="Acceleration"
          setFeatureSettings={setFeatureSettings}
        />
        <InsightMessages
          title="Phone usage"
          description="Please use the form below to define insights for each status level of the phone usage sub score. Each insight include a Main Message and a Hint."
          safeMessagePlaceholder="As a mobile-based insurance provider, we love phones."
          safeHintPlaceholder="And we love it even more that you don’t use your mobile while driving!"
          alertMessagePlaceholder="Did Einstein once saythat only two things are infinite?:"
          alertHintPlaceholder="The Universe and the car insurance price of those who use their mobile phones while driving. "
          dangerMessagePlaceholder="The best place for your eyes is on the road."
          dangerHintPlaceholder="Don’t forget that using a mobile while driving is against law. "
          className="my-8"
          insightSettingsText={
            featureSettings?.insightSettings?.insightSettingsText ?? null
          }
          insightType="PhoneUsage"
          setFeatureSettings={setFeatureSettings}
        />
      </div>
    </Feature>
  );
}
